import { PAYMENT_PROVIDER } from '../../shared/constants';
import { VendingMachine } from '../../shared/types';

/**
 * Which payment gateway a machine uses, and which wallet sheet the device offers.
 *
 * These are two **independent** axes and were previously tangled together. The old
 * shape was four `is<Provider>` flags plus two five-term negation chains
 * (`isApplePaySupported && !isTBC && !isMyPos && !isEMerchantPay && !isMiaPay`),
 * which meant Apple and Google Pay were defined as "everything that is not a named
 * gateway" and every new gateway had to be added to both chains.
 *
 * Modelled properly: a machine has exactly one gateway, and `portmone` — the
 * default — is the one that delegates to whichever wallet sheet the device
 * supports. The same two wallet sheets also serve EMerchantPay, which picks between
 * them internally, so "which sheet" is genuinely not "which gateway".
 *
 * See `.claude/rules/branching-logic.md` Rule 3.
 */
export type PaymentGateway =
    /** MyPOS embedded checkout. */
    | 'mypos'
    /** TBC — redirect to a hosted page. */
    | 'tbc'
    /** MiaPay — redirect to a hosted page. */
    | 'miapay'
    /** EMerchantPay — commits through PHP, picks its own wallet sheet. */
    | 'emerchant'
    /**
     * Tranzzo (SWRN-1543) — the device's own wallet sheet, but the tokens are
     * minted for Tranzzo and committed through the Go payment-service.
     */
    | 'tranzzo'
    /** The default: commit through PHP behind the device's own wallet sheet. */
    | 'portmone';

/**
 * A `Map` rather than an object literal, though no longer for the original reason.
 *
 * It was introduced because `payment_provider_id` was typed `string | number` and
 * the code it replaced compared with `===`, so an object index coercing `'2'` to
 * `2` would have started matching values that never matched. That turned out to be
 * a phantom: the field is `merchants.provider_id`, an `int NULL` column reaching
 * the client in one aliasing hop, so a string cannot arrive. The `Map` stays
 * because `.get()` is typed as possibly-undefined for any key, which an object
 * index is not unless `noUncheckedIndexedAccess` is on — and it is not.
 *
 * The `PAYMENT_PROVIDER` map this reads from is documented in
 * `shared/constants.ts`: it keys on `merchants.provider_id`, and TBC is id **2**,
 * confirmed against production (646 of 648 machines on that id are Georgian
 * companies billing in GEL, one of them named "TBC_Payment"). The `TBC: 4` that
 * used to sit alongside it came from a different enum — web0's
 * `WalletUserOrder::payment_system` — and matched no machine at all.
 *
 * Kaspi (id 3) is absent on purpose, and this is the entry most likely to look
 * like an oversight to a future reader. It is not. Over 90 days its 212 machines
 * took **62,703 orders, 59,772 of them charged** — by volume the busiest gateway
 * we have, and every one of those went through the default branch below. For
 * comparison, TBC has 648 machines and 206 orders in the same window.
 *
 * So the default is Kaspi's working production path. Adding id 3 here would divert
 * roughly 660 orders a day onto a branch nothing has ever exercised. Do not.
 */
const GATEWAY_BY_PROVIDER_ID = new Map<number, PaymentGateway>([
    [PAYMENT_PROVIDER.TBC, 'tbc'],
    [PAYMENT_PROVIDER.MYPOS, 'mypos'],
    [PAYMENT_PROVIDER.EMERCHANTPAY, 'emerchant'],
    [PAYMENT_PROVIDER.MIAPAY, 'miapay'],
    [PAYMENT_PROVIDER.TRANZZO, 'tranzzo'],
]);

export const resolvePaymentGateway = (
    providerId: VendingMachine['payment_provider_id'] | undefined,
): PaymentGateway => {
    if (providerId === null || providerId === undefined) return 'portmone';
    return GATEWAY_BY_PROVIDER_ID.get(providerId) ?? 'portmone';
};

/** Which wallet sheet the browser can put in front of the customer. */
export type WalletSheet = 'apple' | 'google';

/**
 * Apple Pay is offered when the browser exposes `ApplePaySession`; Google Pay is
 * the alternative. Exactly one of the two applies, which is why this returns a
 * value rather than a pair of booleans.
 */
export const resolveWalletSheet = (): WalletSheet =>
    window.ApplePaySession ? 'apple' : 'google';
